// src/components/Header.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { GiftIcon } from "@heroicons/react/24/solid";
import {
  HomeIcon,
  ChartBarIcon,
  TrophyIcon,
  UserCircleIcon,
  BanknotesIcon, 
  XMarkIcon,
  SparklesIcon
} from "@heroicons/react/24/solid";
import { setToken } from "../api";

// Navigation items — "Profile" only renders when authenticated
const NAV = [
  { name: "Home",       href: "#",       icon: HomeIcon },
  // { name: "Leaderboard", href: "#leaderboard", icon: ChartBarIcon },
  // { name: "Tournaments", href: "#tournaments", icon: TrophyIcon },
  
  { name: "Casino",      href: "/casino",      icon: SparklesIcon },
  { name: "Profile",     href: "/profile",     icon: UserCircleIcon },
  { name: "Store",       href: "/buy-chips",   icon: BanknotesIcon },
  { name: "Redeem",     href: "/swap",     icon: GiftIcon },
  // { name: "P2P",     href: "/p2p",     icon: BanknotesIcon },
];

export default function Header() {
  const [open, setOpen]               = useState(false);
  const [activeTab, setActiveTab]     = useState(null);
  const [glitch, setGlitch]           = useState(false);
  const [betMeter, setBetMeter]       = useState(50);
  const [hackText, setHackText]       = useState("ARENA");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail]     = useState("");

  const headerRef = useRef();
  const logoRef   = useRef();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Read auth state + email on mount & every location change
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token);
    const email = localStorage.getItem("email");
    setUserEmail(email || "");
  }, [location]);

  // Handle navigation with hash links
  const handleNavigation = (href) => {
    if (href.startsWith('#')) {
      // If on profile page or any page that's not home, first navigate to home
      if (location.pathname !== '/') {
        navigate('/');
        // Set a small timeout to ensure DOM is ready after navigation
        setTimeout(() => {
          const id = href.slice(1);
          const el = document.getElementById(id);
          if (el && headerRef.current) {
            const headerH = headerRef.current.offsetHeight;
            const top = el.getBoundingClientRect().top + window.scrollY - headerH;
            window.scrollTo({ top, behavior: "smooth" });
          }
        }, 100);
      } else {
        // Already on homepage, just scroll
        const id = href.slice(1);
        const el = document.getElementById(id);
        if (el && headerRef.current) {
          const headerH = headerRef.current.offsetHeight;
          const top = el.getBoundingClientRect().top + window.scrollY - headerH;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
    } else {
      // Regular navigation
      navigate(href);
    }
    setOpen(false);
  };

  // scroll to hash with offset for fixed header
  useEffect(() => {
    if (location.hash && location.pathname === '/') {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el && headerRef.current) {
        const headerH = headerRef.current.offsetHeight;
        const top = el.getBoundingClientRect().top + window.scrollY - headerH;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  }, [location.hash, location.pathname]);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    setToken(null);
    setIsAuthenticated(false);
    navigate("/login");
  };

  // Logo‑magnet, glitch flicker & bet‑meter
  useEffect(() => {
    const onMouse = (e) => {
      const rect = headerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const pos  = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const logo = logoRef.current;
      if (!logo) return;
      const lr = logo.getBoundingClientRect();
      const lc = {
        x: lr.left + lr.width/2 - rect.left,
        y: lr.top  + lr.height/2 - rect.top
      };
      const dist = Math.hypot(pos.x - lc.x, pos.y - lc.y);
      if (dist < 150) {
        const pull = (1 - dist/150)*0.2;
        logo.style.transform = `translate(${(pos.x-lc.x)*pull}px, ${(pos.y-lc.y)*pull}px)`;
      } else {
        logo.style.transform = "translate(0,0)";
      }
    };

    const glitchInt = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 200 + Math.random()*300);
    }, 3000);

    const meterInt = setInterval(() => {
      setBetMeter(m => Math.max(10, Math.min(100, m + (Math.random()*6 - 3))));
    }, 700);

    document.addEventListener("mousemove", onMouse);
    return () => {
      document.removeEventListener("mousemove", onMouse);
      clearInterval(glitchInt);
      clearInterval(meterInt);
    };
  }, []);

  // Scramble text effect
  const scramble = () => {
    const target = "ARENA";
    const chars  = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let it = 0;
    const iv = setInterval(() => {
      it++;
      let s = "";
      for (let i = 0; i < target.length; i++) {
        s += i < it/2
          ? target[i]
          : chars[Math.floor(Math.random()*chars.length)];
      }
      setHackText(s);
      if (it > target.length*2) {
        clearInterval(iv);
        setHackText(target);
      }
    }, 50);
  };

  // Trigger scramble on mount + every 2s
  useEffect(() => {
    scramble();
    const hi = setInterval(scramble, 2000);
    return () => clearInterval(hi);
  }, []);

  return (
    <header
      ref={headerRef}
      className="relative h-24 overflow-hidden bg-gray-900 bg-opacity-80 flex items-center px-8 font-mono fixed top-0 left-0 right-0 z-40"
    >
      {/* Logo */}
      <div
        ref={logoRef}
        onClick={() => {
          scramble();
          navigate('/');
        }}
        className={`relative z-20 cursor-pointer ${glitch ? "glitch-effect" : ""}`}
      >
        <div className="relative px-4 py-2 bg-gray-900 bg-opacity-60 border border-green-400/30 backdrop-blur-sm rounded">
          <div className="absolute inset-0 noise-bg" />
          <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500">
            {hackText}
          </h1>
          {/* Bet‑meter */}
          <div className="absolute right-0 top-0 bottom-0 w-1 bg-gray-800 overflow-hidden rounded-l">
            <div
              className="absolute bottom-0 w-full bg-gradient-to-t from-green-500 to-green-600 transition-all duration-300"
              style={{ height: `${betMeter}%` }}
            />
          </div>
        </div>
      </div>

      {/* Desktop nav */}
      <nav className="hidden md:flex ml-auto items-center space-x-4 z-10">
        {NAV.map(({ name, href, icon: Icon }, i) => {
          // Skip profile if not authenticated
          if (name === "Profile" && !isAuthenticated) return null;
          
          const isActiveLink = href.startsWith("#")
            ? location.hash === href && location.pathname === '/'
            : location.pathname === href;
          const isActive = isActiveLink || activeTab === i;

          return (
            <button
              key={name}
              onClick={() => handleNavigation(href)}
              onMouseEnter={() => setActiveTab(i)}
              onMouseLeave={() => setActiveTab(null)}
              className={`flex items-center px-3 py-2 rounded-lg transition-all duration-300 ${
                isActive
                  ? "text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500"
                  : "text-white hover:text-gray-300"
              }`}
            >
              <Icon
                className={`w-5 h-5 mr-2 ${
                  isActive
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500"
                    : "text-white"
                }`}
              />
              <span className="font-medium">{name}</span>
            </button>
          );
        })}

        {!isAuthenticated ? (
          <>
            <Link
              to="/signup"
              className="px-3 py-2 rounded-lg text-white hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-violet-500 hover:via-pink-500 hover:to-red-500 transition"
            >
              Sign Up
            </Link>
            <Link
              to="/login"
              className="px-3 py-2 rounded-lg text-white hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-violet-500 hover:via-pink-500 hover:to-red-500 transition"
            >
              Log In
            </Link>
          </>
        ) : (
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-white hover:text-red-400 transition"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5" />
          Log Out
        </button>
        )}
      </nav>

      {/* Mobile menu button */}
      <button
        className="md:hidden ml-auto z-20 morph-container"
        onClick={() => setOpen(true)}
      >
        <div className="morph-cube">
          <div className="face front flex flex-col items-center justify-center gap-1 p-2">
            <span className="w-5 h-0.5 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500" />
            <span className="w-5 h-0.5 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500" />
            <span className="w-5 h-0.5 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500" />
          </div>
          <div className="face back flex items-center justify-center">
            <span className="bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-transparent text-xs">
              MENU
            </span>
          </div>
          <div className="face left" />
          <div className="face right" />
          <div className="face top" />
          <div className="face bottom" />
        </div>
      </button>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-50 neural-interface">
          <div className="absolute inset-0 bg-gray-900 bg-opacity-90 neural-net" />
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="absolute top-4 right-4 z-50 p-2 bg-gray-800 bg-opacity-50 rounded-full hover:bg-opacity-75 transition"
          >
            <XMarkIcon className="w-5 h-5 text-white" />
          </button>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="relative w-4/5 max-w-sm">
              <div className="absolute -inset-px border border-green-500/50 rounded-lg pulse-slow" />
              <div className="relative bg-gray-900 bg-opacity-70 backdrop-blur-lg rounded-lg p-8 border border-green-800/30">
                <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 mb-6 text-center tracking-widest">
                  NEURAL MENU
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-violet-500 to-transparent mt-1" />
                </h2>
                {isAuthenticated && (
                  <div className="text-center text-gray-300 mb-4">
                    Welcome, <span className="text-violet-400">{userEmail}</span>!
                  </div>
                )}
                <div className="space-y-4">
                  {NAV.map(({ name, href, icon: Icon }, i) => {
                    if (name === "Profile" && !isAuthenticated) return null;
                    const isActiveLink = href.startsWith("#")
                      ? location.hash === href && location.pathname === '/'
                      : location.pathname === href;
                    return (
                          <button
                            key={name}
                            onClick={() => handleNavigation(href)}
                            className={`relative block w-full p-3 rounded-lg overflow-hidden neural-link ${
                              isActiveLink
                                ? "text-violet-400"
                                : "text-white"
                            }`}
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 border border-green-700/30 rounded-lg transform transition-all duration-300 hover:border-violet-500/50" />
                          <div className="relative flex items-center space-x-4">
                          <Icon className={`w-6 h-6 ${isActiveLink ? 'text-violet-400' : 'text-white'}`} />
                          <span className="font-medium">{name}</span>
                        </div>
                      </button>
                    );
                  })}

                  {!isAuthenticated && (
                    <div className="mt-4 space-y-2">
                      <Link
                        to="/signup"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-lg text-white text-center hover:text-violet-400 transition"
                      >
                        Sign Up
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-lg text-white text-center hover:text-violet-400 transition"
                      >
                        Log In
                      </Link>
                    </div>
                  )}
                  {isAuthenticated && (
                    <button
                      onClick={() => { handleLogout(); setOpen(false); }}
                      className="w-full px-3 py-2 rounded-lg text-white text-center hover:text-red-400 transition"
                    >
                      Log Out
                    </button>
                  )}
                </div>
                {/* <div className="mt-6 pt-4 border-t border-green-900/50 flex justify-between text-xs text-white">
                  <span>SYS::ONLINE</span>
                  <span className="data-stream">PING::{Math.floor(Math.random() * 100)}ms</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Center‑fading gradient line */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent" />

      {/* All CSS */}
      <style jsx>{`
        /* neural‑grid */
        .neural-net {
          background-image:
            radial-gradient(circle, rgba(74,222,128,0.1) 1px, transparent 1px),
            radial-gradient(circle, rgba(74,222,128,0.05) 3px, transparent 3px);
          background-size: 30px 30px, 90px 90px;
          animation: neuralShift 60s linear infinite;
        }
        @keyframes neuralShift {
          0% { background-position: 0 0, 15px 15px; }
          100% { background-position: 30px 30px, 45px 45px; }
        }
        .glitch-effect {
          animation: glitch 0.2s ease infinite;
        }
        @keyframes glitch {
          0% { transform: translate(0,0); filter: hue-rotate(0deg); }
          20% { transform: translate(-2px,2px); filter: hue-rotate(90deg); }
          40% { transform: translate(2px,-2px); filter: hue-rotate(180deg); }
          60% { transform: translate(-2px,-2px); filter: hue-rotate(270deg); }
          80% { transform: translate(2px,2px); filter: hue-rotate(360deg); }
          100% { transform: translate(0,0); filter: hue-rotate(0deg); }
        }
        .noise-bg {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)'/%3E%3C/svg%3E");
          mix-blend-mode: overlay;
          opacity: 0.05;
        }
        .pulse-slow {
          animation: pulse 4s ease-in-out infinite;
        }
        @keyframes pulse {
          0%,100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }
        .data-transfer {
          height: 100%;
          width: 30px;
          background: linear-gradient(to right, transparent, rgba(74,222,128,0.9), transparent);
          animation: transfer 1.5s linear infinite;
        }
        @keyframes transfer {
          0% { transform: translateX(-30px); }
          100% { transform: translateX(100%); }
        }
        .data-stream {
          position: relative;
        }
        .data-stream::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 4px;
          height: 100%;
          background: rgba(74,222,128,0.7);
          animation: blink 1s steps(1) infinite;
        }
        @keyframes blink {
          0%,100% { opacity: 0; }
          50% { opacity: 1; }
        }
        .morph-container {
          perspective: 600px;
        }
        .morph-cube {
          width: 42px;
          height: 42px;
          transform-style: preserve-3d;
          transform: rotateX(-20deg) rotateY(25deg);
          transition: transform 0.5s ease;
        }
        .morph-cube:hover {
          transform: rotateX(-5deg) rotateY(45deg);
        }
        .face {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.7);
          border: 1px solid rgba(74,222,128,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .front { transform: translateZ(21px); }
        .back { transform: translateZ(-21px) rotateY(180deg); }
        .left { transform: translateX(-21px) rotateY(-90deg); }
        .right { transform: translateX(21px) rotateY(90deg); }
        .top { transform: translateY(-21px) rotateX(90deg); }
        .bottom { transform: translateY(21px) rotateX(-90deg); }
        .neural-interface {
          animation: fadeIn 0.3s ease forwards;
        }
        .neural-link {
          opacity: 0;
          transform: translateY(10px);
          animation: slideIn 0.4s ease forwards;
        }
        @keyframes slideIn {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity:0; }
          to { opacity:1; }
        }
      `}</style>
    </header>
  );
}
